import React from 'react';
import { Box, Grid, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import ItemIssueHistoryTab from '@/modules/inventory/items/components/ItemIssueHistoryTab';
import ItemReceiptHistoryTab from '@/modules/inventory/items/components/ItemReceiptHistoryTab';

import { InventoryStockBalanceFormData } from '../types';

interface StockBalanceFormProps {
  formData: InventoryStockBalanceFormData;
  stockBalanceId?: string;
  canEdit?: boolean;
  onChangeForm: (updates: Partial<InventoryStockBalanceFormData>) => void;
  onSave: () => void;
  onCancel?: () => void;
}

export default function StockBalanceForm({
  formData,
  stockBalanceId,
  canEdit = false,
  onChangeForm,
  onSave,
  onCancel = () => {},
}: StockBalanceFormProps) {
  const [activeSection, setActiveSection] = React.useState(0);
  void canEdit;
  void onSave;

  const onHandQty = Number(formData.on_hand_qty) || 0;
  const allocatedQty = Number(formData.allocated_qty) || 0;
  const availableQty = Number(formData.available_qty) || (onHandQty - allocatedQty);
  const systemInfo = [
    formData.company_name ? `Công ty: ${formData.company_name}` : '',
    formData.last_received_at ? `Lần nhập gần nhất: ${formData.last_received_at}` : '',
    formData.last_movement_at ? `Lần biến động gần nhất: ${formData.last_movement_at}` : '',
    formData.updated_at ? `Cập nhật hệ thống: ${formData.updated_at}` : '',
  ].filter(Boolean).join('\n');

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitDisabled
      showSubmitButton={false}
      hideActionBar={activeSection !== 0}
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper sx={{ mb: 2, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Tabs
          value={activeSection}
          onChange={(_event, value: number) => setActiveSection(value)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{
            px: 1,
            minHeight: 44,
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { minHeight: 44, px: 2.5, fontWeight: 600, textTransform: 'none' },
          }}
        >
          <Tab label='Tồn kho' />
          <Tab label='Nhập kho' />
          <Tab label='Xuất kho' />
        </Tabs>
      </Paper>

      <Box sx={{ display: activeSection === 0 ? 'block' : 'none' }}>
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã vật tư' labelWidth={100}>
              <TextField size='small' value={formData.item_code} onChange={(event) => onChangeForm({ item_code: event.target.value })} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Tên vật tư' labelWidth={100}>
              <TextField size='small' value={formData.item_name} onChange={(event) => onChangeForm({ item_name: event.target.value })} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Mã kho' labelWidth={100}>
              <TextField size='small' value={formData.store_code} onChange={(event) => onChangeForm({ store_code: event.target.value })} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Tên kho' labelWidth={100}>
              <TextField size='small' value={formData.store_name} onChange={(event) => onChangeForm({ store_name: event.target.value })} disabled fullWidth />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Lô hàng' labelWidth={100}>
              <TextField size='small' value={formData.lot_no} onChange={(event) => onChangeForm({ lot_no: event.target.value })} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Vị trí kệ' labelWidth={100}>
              <TextField size='small' value={formData.bin_location} onChange={(event) => onChangeForm({ bin_location: event.target.value })} disabled fullWidth />
            </LabeledField>
            <LabeledField label='Giá gần nhất' labelWidth={100}>
              <TextField
                type='number'
                size='small'
                value={formData.last_price}
                onChange={(event) => onChangeForm({ last_price: event.target.value })}
                disabled
                fullWidth
              />
            </LabeledField>

            <Grid container rowSpacing={1} columnSpacing={5}>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='Tồn thực tế' labelWidth={100}>
                  <TextField
                    type='number'
                    size='small'
                    value={formData.on_hand_qty}
                    onChange={(event) => onChangeForm({
                      on_hand_qty: event.target.value,
                      available_qty: String((Number(event.target.value) || 0) - allocatedQty),
                    })}
                    disabled
                    fullWidth
                  />
                </LabeledField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='Đã gán' labelWidth={80}>
                  <TextField
                    type='number'
                    size='small'
                    value={formData.allocated_qty}
                    onChange={(event) => onChangeForm({
                      allocated_qty: event.target.value,
                      available_qty: String(onHandQty - (Number(event.target.value) || 0)),
                    })}
                    disabled
                    fullWidth
                  />
                </LabeledField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LabeledField label='Khả dụng' labelWidth={80}>
                  <TextField type='number' size='small' value={Number.isFinite(availableQty) ? availableQty : 0} disabled fullWidth />
                </LabeledField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} sx={sharedFormStyles.additionalTextGrid}>
          <Grid size={{ xs: 12 }}>
            <LabeledField label='' labelWidth={0} alignItems='start'>
              <Typography
                component='div'
                sx={{
                  minHeight: 88,
                  width: '100%',
                  whiteSpace: 'pre-line',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: '#374151',
                  p: '10px 12px',
                  boxSizing: 'border-box',
                }}
              >
                {systemInfo || 'Số dư tồn kho được tính tự động từ các chứng từ nhập, xuất, hoàn trả và kiểm kê đã ghi sổ.'}
              </Typography>
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>
      </Box>

      <Box sx={{ display: activeSection === 1 ? 'block' : 'none' }}>
        <Paper sx={{ ...sharedFormStyles.formCard, width: '100%', boxSizing: 'border-box', p: 2 }}>
          <ItemReceiptHistoryTab itemId={formData.item_id} companyId={formData.company_id} active={activeSection === 1} />
        </Paper>
      </Box>

      <Box sx={{ display: activeSection === 2 ? 'block' : 'none' }}>
        <Paper sx={{ ...sharedFormStyles.formCard, width: '100%', boxSizing: 'border-box', p: 2 }}>
          <ItemIssueHistoryTab itemId={formData.item_id} companyId={formData.company_id} active={activeSection === 2} />
        </Paper>
      </Box>
    </AppForm>
  );
}
